<template>
  <!-- the image collections' header (Photos, Posters, Videos: PageHeader.vue), its controls on
       the right in the same order and look (Seán, 2026-11-03): the sort first, as Posters' view;
       then Show All and the filters, a chosen one its green tag -->
  <PageHeader title="People" :to="`/${route.params.year}/People`">
    <Dropdown
      v-show="!category && !day"
      chevron
      placeholder="Sort by"
      :options="['First name', 'Last name', 'Session']"
      :selected="sortBy"
      :tag="false"
      @update="selectSort" />
    <ButtonBespoke v-if="category || day" id="showAll" @click="showAll">Show All</ButtonBespoke>
    <Dropdown
      v-show="!category"
      chevron
      placeholder="Day"
      :options="dayOptions"
      :separators="daySeparators"
      :selected="day"
      @update="selectDay" />
    <Dropdown
      v-show="!day"
      chevron
      placeholder="Category"
      :options="sessionMenu.options"
      :separators="sessionMenu.separators"
      :selected="category"
      @update="selectCategory" />
  </PageHeader>
  <!-- currently, highlights only on localhost and dev.vizbi.org -->
  <!--   "host !== 'vizbi.org'"   -->
  <!-- hidden, not removed, while an attendee section is the filter: they still report the
       sessions and categories the filter lists -->
  <div v-show="!isAttendeeFilter">
    <PeopleGallery
      name="Speakers"
      :highlights="true"
      :category="category"
      :day="day"
      :day-names="dayNames"
      :show-tags="true"
      :sort="sortBy"
      @foundDays="accumulateDays"
      @updateDay="selectDay"
      @foundCategories="accumulateSessions"
      @updateCategory="selectCategory" />
    <PeopleGallery
      name="Session Chairs"
      :category="category"
      :day="day"
      :day-names="dayNames"
      :show-tags="true"
      :sort="sortBy"
      @foundDays="accumulateDays"
      @updateDay="selectDay"
      @foundCategories="accumulateSessions"
      @updateCategory="selectCategory" />
    <PeopleGallery
      name="Organizers"
      :category="category"
      :day="day"
      :day-names="dayNames"
      :show-tags="true"
      :sort="sortBy"
      @foundCategories="accumulateCategories"
      @updateCategory="selectCategory" />
  </div>
  <!-- each event's attendees, for its own attendees only (fullstack claude/ATTENDEES-SPEC.md):
       hidden while a session or day is chosen, alone when one of them is the filter -->
  <template v-for="section in attendeeSections" :key="section.kind">
    <PeopleAttendees
      v-if="(!day || dayKinds.includes(section.kind)) && getIsShownFor(section)"
      :kind="section.kind"
      :label="section.label"
      :sort="sortBy" />
  </template>
</template>

<script setup>
const log = (msg) => process.env.VUE_APP_DEV && console.log(); // eslint-disable-line

import ButtonBespoke from "@/components/ButtonBespoke";
import PageHeader from "@/components/PageHeader.vue";
import {DELIVERIES, getSessionMenu} from "@/composables/usePeopleSessions";
import usePeopleDays from "@/composables/usePeopleDays";
import Dropdown from "@/components/Dropdown.vue";
import {useSortSessionNames} from "@/composables/useSortSessionNames";

import PeopleGallery from "@/components/People/Gallery.vue";
import PeopleAttendees from "@/components/People/Attendees.vue";
import useAttendees, {
  getIsContactHash,
  getLabelFromHash,
  ALL_ATTENDEES, getIsAttendeeFilter
} from "@/composables/useAttendees";
import stringify from "json-stringify-safe";
import {computed, ref, onMounted, watch} from "vue";
import {useRoute} from "vue-router";

const category = ref("");
const categories = ref([]);
const dates = ref([]);
const day = ref("");
const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const sessions = ref([]);
const sortBy = ref(localStorage.getItem("sortBy") || "Session");

const convertToDay = (dateString) => dateString;

const route = useRoute();
const {sections: attendeeSections} = useAttendees(() => Number(route.params.year));
const isAttendeeFilter = computed(() => getIsAttendeeFilter(category.value));
// the Day menu: the program's days in the chosen time zone (usePeopleDays.js); a year whose
// program gives none, the people sheet's own dates (the galleries' foundDays)
const programDays = usePeopleDays(() => Number(route.params.year));
const dayOptions = computed(() => {
  return programDays.value.length ? programDays.value.map(({label}) => label) : dates.value;
});
// a line under each event's last day: the in-person Masterclass, Workshop, the virtual events,
// the online ones (usePeopleDays.js getGroup)
const daySeparators = computed(() => programDays.value.filter((day, index, all) => {
  return all[index + 1] && all[index + 1].group !== day.group;
}).map(({label}) => label));
const selectedDay = computed(() => programDays.value.find(({label}) => label === day.value));
const dayNames = computed(() => (selectedDay.value ? [...selectedDay.value.names] : null));
const dayKinds = computed(() => selectedDay.value?.kinds || []);
// a program day's label in the address: "Tue-Apr-14" ("_" there means a person)
const DAY_LABEL = /^[A-Z][a-z]{2} [A-Z][a-z]{2} \d+$/;
const getDayFromHash = (text) => {
  const label = String(text || "").replace(/-/g, " ");
  return DAY_LABEL.test(label) ? label : "";
};

// an attendee section under the Session filter: none chosen, itself, or its event's session
// ("Online Masterclass": its speakers and its attendees; Seán, 2026-11-03)
const getIsShownFor = (section) => {
  const names = [ALL_ATTENDEES, section.label, ...section.sessions];
  return !category.value || names.includes(category.value);
};

// a session in the address with spaces as "_" ("#Online_Masterclass", as setFragment writes
// it), which reads as a person until the sessions are known: applied once they are
let pendingSession = "";
const getIsKnownCategory = (name) => sessions.value.includes(name) || DELIVERIES.includes(name);
const getSessionFromHash = (hash) => {
  const text = decodeURIComponent(String(hash || "")).replace(/^#/, "").split("&")[0];
  return text.includes("_") ? text.replace(/_/g, " ") : "";
};

// grouped as /Videos' Session menu (usePeopleSessions.js), the attendee sections last
const sessionMenu = computed(() => {
  // one item for the sections, only for those who may see one (useAttendees.js: the server's)
  return getSessionMenu(sessions.value, attendeeSections.value.length ? [ALL_ATTENDEES] : []);
});

const accumulateDays = (dateStrings) => {
  log(`People: dates = ${JSON.stringify(dateStrings)}`);
  log(`People: dates.value = ${JSON.stringify(dates.value)}`);
  dateStrings.forEach((dateString) => {
    const day = convertToDay(dateString);
    log(`People: [dateString, day] = [${dateString}, ${day}]`);
    if (dates.value.includes(day)) return;
    dates.value.push(day);
  });
};

const regularizeCategoryNames = (category) => {
  if (category === "Cellular Systems") return "Cells";
  if (category === "Tissues & Organisms") return "Tissues";
  if (category === "Populations & Ecosystems") return "Ecosystems";
  return category;
};

const accumulateCategories = (mergedCategories) => {
  log(`accumulateCategories(${stringify(mergedCategories)})`);
  if (sessions.value.length > 0) {
    mergedCategories = mergedCategories.concat(sessions.value);
    log(`accumulateCategories: session = ${stringify(sessions.value)}`);
  }
  if (categories.value.length > 0) {
    mergedCategories = mergedCategories.concat(categories.value);
    log(`accumulateCategories: categories = ${stringify(categories.value)}`);
  }
  const uniqueCategories = [];
  mergedCategories.forEach((category) => {
    category = regularizeCategoryNames(category);
    if (!uniqueCategories.includes(category)) uniqueCategories.push(category);
  });
  categories.value = uniqueCategories;
  log(`categories = ${stringify(categories.value)}`);
  if (getIsAttendeeFilter(category.value)) return; // the attendees: not a gallery's
  if (DELIVERIES.includes(category.value)) return; // a delivery: the menu's, from the sessions
  if (category.value && !categories.value.includes(category.value)) {
    log(`category is set to something weird - get rid of it`);
    category.value = "";
    clearFragment();
  }
};

const getSessionParts = (rawSession) => {
  const text = String(rawSession || "");
  const parts = text
    .split(/[;,]/g)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts : [];
};

const accumulateSessions = (sessionsToAdd) => {
  log(`accumulateSessions called`);
  sessionsToAdd.forEach((rawSession) => {
    const parts = getSessionParts(rawSession);
    parts.forEach((session) => {
      session = regularizeCategoryNames(session);
      if (!getIsKnownCategory(session)) sessions.value.push(session);
    });
  });
  sessions.value = useSortSessionNames(sessions.value);
  if (!pendingSession || !getIsKnownCategory(pendingSession)) return;
  [category.value, pendingSession] = [pendingSession, ""];
  setFragment(category.value); // Person/Highlight.vue cleared it, taking it for a name
};

const setFragment = async (selection) => {
  log(`People: setFragment(${selection})`);
  log(`People: setFragment: path = ${route.path}`);
  // an attendee section's with "-": an "_" in the address means a person (Person/Highlight.vue)
  const isOwn = getIsAttendeeFilter(selection) || DAY_LABEL.test(selection);
  const fragment = selection.replace(/ /g, isOwn ? "-" : "_");
  history.pushState({}, null, route.path + "#" + fragment);
  const url = "#" + fragment;
  history.replaceState(history.state, "", url);
};

const clearFragment = () => {
  log(`clearFragment()`);
  history.pushState({}, null, window.location.pathname + window.location.search);
};

const selectCategory = (categorySelected) => {
  log(`People: selectCategory(${categorySelected})`);
  if (!categorySelected) {
    category.value = "";
    day.value = "";
    return clearFragment();
  }
  category.value = categorySelected;
  day.value = "";
  setFragment(categorySelected);
};

const selectDay = (daySelected) => {
  log(`People: selectDay(${daySelected})`);
  if (!daySelected) {
    day.value = "";
    category.value = "";
    return clearFragment();
  }
  day.value = daySelected;
  category.value = "";
  setFragment(daySelected);
};

const selectSort = (method) => {
  if (!method) return (sortBy.value = "Session");
  sortBy.value = method;
  localStorage.setItem("sortBy", method);
};

const showAll = () => {
  day.value = "";
  category.value = "";
  clearFragment();
};

// the address as the page opens: read now, as the galleries' Person/Highlight.vue clears an
// "_" one (taking it for a person's name) before this page's onMounted runs
const arrivalHash = window.location.hash;

onMounted(() => {
  log(`People.vue/mounted()`);
  log(`('${category.value}', '${day.value}', '${sortBy.value}')`);
  log(`categories = ${stringify(categories.value)}`);
  if (!arrivalHash) return;
  let hash = decodeURIComponent(arrivalHash);
  log(`URL contains hash: '${hash}'`);
  hash = hash.split(/&/)[0].replace(/#/, "");
  log(`first hash parameter = ${hash}`);
  // an attendee section, or the Contact dialog open over one (its "from="): remounted as the
  // dialog hides the scrollbar, which changes the page's key (Main.vue, getPageKey)
  if (getIsContactHash(arrivalHash)) return (category.value = getLabelFromHash(arrivalHash));
  const attendeeLabel = getLabelFromHash(hash);
  if (attendeeLabel) return (category.value = attendeeLabel);
  if (getDayFromHash(hash)) return (day.value = getDayFromHash(hash));
  if (hash.match(/_/)) {
    log(`first parameter contains underscore - a person's name, or a session's`);
    pendingSession = hash.replace(/_/g, " ");
    return;
  }
  if (days.includes(hash)) {
    log(`first parameter is a day`);
    day.value = hash;
    return;
  }
  log(`for now, assume first parameter is a category`);
  category.value = hash;
  log(`hash: category = ${category.value}`);
});

watch(route, (to, from) => {
  log(`people: change from '${from.name}' to '${to.name}'`);
  if (!to.name.match(/people/i) || from.name.match(/people/i)) return;
  const attendeeLabel = getLabelFromHash(window.location.hash); // e.g. from /live's See Attendees
  if (attendeeLabel) return ([category.value, day.value] = [attendeeLabel, ""]);
  const session = getSessionFromHash(window.location.hash); // "#Online_Masterclass"
  if (session && getIsKnownCategory(session)) {
    [category.value, day.value] = [session, ""];
    return setFragment(session);
  }
  if (session) pendingSession = session;
  if (day.value) return setFragment(day.value);
  if (category.value) return setFragment(category.value);
});
</script>

<style scoped>

#organizerButton {
  margin-left: 20px;
  font-size: 16px;
  padding: 10px 20px 10px 10px;
  text-transform: none;
  font-weight: 300;
}

.organizerTitle {
  background: #ff43bb;
  padding: 4px;
  border-radius: 3px;
  color: white;
}

#showAll {
  /* margin-left: 15px;
    font-size: 14px;
    padding: 8px 5px; */
  margin-left: 20px;
  font-size: 0.9rem;
  padding: 10px;
  text-transform: none;
  font-weight: 300;
}
</style>
